const DECIMAL_PATTERN = /^(?:0|[1-9][0-9]*)(?:\.([0-9]{1,4}))?$/;

const decimalUnits = (value) => {
  const normalized = String(value ?? "").trim();
  const match = normalized.match(DECIMAL_PATTERN);
  if (!match) return null;
  const [whole] = normalized.split(".");
  const fraction = String(match[1] || "").padEnd(4, "0");
  const units = Number(whole) * 10000 + Number(fraction || "0");
  return Number.isSafeInteger(units) ? units : null;
};

const decimalString = (units) => {
  const safe = Math.max(0, units);
  const whole = Math.floor(safe / 10000);
  const fraction = String(safe % 10000).padStart(4, "0");
  return `${whole}.${fraction}`;
};

const partyKey = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\b(?:l\.?l\.?c|ltd|limited|gmbh|sh\.?p\.?k|inc|company|corp(?:oration)?|group)\b/g, " ")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

export const findPurchaseVendor = (item, vendors = []) => {
  const evidence = item?.accountingEvidence || {};
  const supplierKey = partyKey(evidence.supplier);
  if (!supplierKey) return null;
  return vendors.find((vendor) => vendor.businessId === item.businessId && partyKey(vendor.legalName || vendor.name) === supplierKey) || null;
};

export const purchaseDocumentFromIntake = ({ item, vendors = [], organizationId }) => {
  const evidence = item?.accountingEvidence || {};
  const gross = decimalUnits(evidence.grossAmount);
  if (!item?.id || !organizationId || !item.businessId || gross === null || gross <= 0) return null;

  const suppliedTax = decimalUnits(evidence.taxAmount);
  if (String(evidence.taxAmount ?? "").trim() && (suppliedTax === null || suppliedTax > gross)) return null;
  const tax = suppliedTax !== null && suppliedTax >= 0 && suppliedTax <= gross ? suppliedTax : 0;
  const suppliedNet = decimalUnits(evidence.netAmount);
  if (String(evidence.netAmount ?? "").trim() && (suppliedNet === null || suppliedNet + tax !== gross)) return null;
  if (!/^[A-Z]{3}$/.test(String(evidence.currency || "").trim().toUpperCase())) return null;
  const reconciledNet = suppliedNet !== null && suppliedNet + tax === gross ? suppliedNet : gross - tax;
  const vendor = findPurchaseVendor(item, vendors);
  const documentType = item.documentType === "Receipt" ? "receipt" : "purchase_invoice";
  const reference = String(evidence.reference || "").trim();
  const description = reference
    ? `${item.documentType} ${reference}`
    : `${item.documentType} from ${String(evidence.supplier || "supplier").trim()}`;

  return {
    organizationId,
    expectedRevision: item.revision,
    expectedExtractionRunId: item.extraction?.id ?? null,
    businessId: item.businessId,
    vendorId: vendor?.id || null,
    vendorName: String(evidence.supplier || "").trim(),
    documentType,
    sourceKind: "intake",
    intakeRecordId: item.id,
    documentNumber: reference || null,
    externalReference: null,
    issuedOn: String(evidence.documentDate || "").trim(),
    dueOn: String(evidence.dueDate || "").trim() || null,
    currency: String(evidence.currency || "EUR").trim().toUpperCase(),
    description,
    deductibility: "not_reviewed",
    deductibilityNote: null,
    lines: [{
      description,
      quantity: "1.0000",
      unit: "item",
      unitPrice: decimalString(reconciledNet),
      expenseCategory: null,
      generalLedgerAccount: null,
      isFixedAsset: false,
    }],
    taxLines: tax > 0 ? [{
      label: "Tax from source document",
      rate: null,
      amount: decimalString(tax),
      recoverableAmount: "0.0000",
    }] : [],
  };
};

export const purchaseBalance = (document) => Math.max(0, Number(document?.grossAmount || 0) - Number(document?.paidAmount || 0));
